import React, { useEffect } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { Autocomplete, Avatar, Stack, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { CancelButton, SubmitButton, UpdateButton } from '../../../components/mui/Buttons/Buttons';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import {
  createMilestoneAchievement,
  updateMilestoneAchievement,
  getMilestoneEmployees,
} from '../../../redux/features/milestoneAchievementSlice';

/**
 * add / edit one milestone achievement. `row` present -> edit mode.
 * @author Akhil E M
 * @createdAt 27-08-2026
 * @updates 27-08-2026(Akhil EM),
 * @type component
 */
const MilestoneAddEdit = ({ row, onClose }) => {
  const dispatch = useDispatch();
  const handleApiResponse = useHandleApiResponse();
  const { mutating, employees } = useSelector((state) => state.milestoneAchievement);
  const isEdit = Boolean(row?.milestone_achievement_id);

  useEffect(() => {
    //THE LIST IS SMALL AND UNPAGINATED , SO IT IS FETCHED ONCE AND FILTERED CLIENT SIDE
    if (!employees.data.length) dispatch(getMilestoneEmployees());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: row?.user_id ?? '',
      milestone_title: row?.milestone_title ?? '',
      milestone_description: row?.milestone_description ?? '',
      milestone_reward: row?.milestone_reward ?? '',
      date_of_publish: row?.date_of_publish ? dayjs(row.date_of_publish) : null,
    },
    validationSchema: Yup.object({
      user_id: Yup.number().required('Employee is required'),
      milestone_title: Yup.string().trim().required('Milestone title is required'),
      milestone_description: Yup.string().nullable(),
      milestone_reward: Yup.string().nullable(),
      date_of_publish: Yup.mixed()
        .required('Date of publish is required')
        .test('valid', 'Date of publish is not a valid date', (v) => !v || dayjs(v).isValid()),
    }),
    onSubmit: async (values) => {
      const payload = {
        user_id: values.user_id,
        milestone_title: values.milestone_title.trim(),
        milestone_description: values.milestone_description || null,
        milestone_reward: values.milestone_reward || null,
        //SENT AS A PLAIN DATE . THE COLUMN IS A DATE , SO A TIMEZONE CARRYING ISO STRING
        //COULD LAND THE MILESTONE ON THE WRONG DAY.
        date_of_publish: dayjs(values.date_of_publish).format('YYYY-MM-DD'),
      };
      const res = isEdit
        ? await dispatch(
            updateMilestoneAchievement({
              ...payload,
              milestone_achievement_id: row.milestone_achievement_id,
            }),
          )
        : await dispatch(createMilestoneAchievement(payload));
      handleApiResponse(res, { onSuccess: () => onClose(true) });
    },
  });

  const { errors, touched, values, getFieldProps, setFieldValue, handleSubmit } = formik;
  const selectedEmployee = employees.data.find((e) => e.user_id === values.user_id) ?? null;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Autocomplete
            options={employees.data}
            loading={employees.loading}
            value={selectedEmployee}
            //ROW EDIT CAN ARRIVE BEFORE THE EMPLOYEE LIST DOES , SO MATCH ON ID NOT OBJECT
            isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
            getOptionLabel={(option) => option?.name ?? ''}
            onChange={(e, value) => setFieldValue('user_id', value?.user_id ?? '')}
            renderOption={(props, option) => (
              <Stack {...props} key={option.user_id} direction="row" spacing={1.5} alignItems="center">
                <Avatar src={option.photo_url || undefined} sx={{ width: 28, height: 28 }}>
                  {option.name?.[0] ?? '?'}
                </Avatar>
                <Stack spacing={0} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {option.name}
                  </Typography>
                  {option.emp_id ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {option.emp_id}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee *"
                error={Boolean(touched.user_id && errors.user_id)}
                helperText={touched.user_id && errors.user_id}
              />
            )}
          />

          <TextField
            fullWidth
            label="Milestone Title *"
            {...getFieldProps('milestone_title')}
            error={Boolean(touched.milestone_title && errors.milestone_title)}
            helperText={touched.milestone_title && errors.milestone_title}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            {...getFieldProps('milestone_description')}
            error={Boolean(touched.milestone_description && errors.milestone_description)}
            helperText={touched.milestone_description && errors.milestone_description}
          />

          <TextField
            fullWidth
            label="Reward"
            {...getFieldProps('milestone_reward')}
            error={Boolean(touched.milestone_reward && errors.milestone_reward)}
            helperText={touched.milestone_reward && errors.milestone_reward}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date of Publish *"
              format="DD/MM/YYYY"
              value={values.date_of_publish}
              onChange={(value) => setFieldValue('date_of_publish', value)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(touched.date_of_publish && errors.date_of_publish),
                  helperText:
                    (touched.date_of_publish && errors.date_of_publish) ||
                    'The employee is celebrated once, on their first login on or after this date',
                },
              }}
            />
          </LocalizationProvider>

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
            <CancelButton action={() => onClose(false)}>Cancel</CancelButton>
            {isEdit ? (
              <UpdateButton disabled={mutating} type="submit">
                {mutating ? 'Updating...' : 'Update'}
              </UpdateButton>
            ) : (
              <SubmitButton disabled={mutating} type="submit">
                {mutating ? 'Submitting...' : 'Submit'}
              </SubmitButton>
            )}
          </Stack>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

export default MilestoneAddEdit;
